import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import clsx from 'clsx';
import {
  getDocumentFileType,
  getDocumentId,
  isDocumentsFolder,
} from '../../../../utils/documents';
import useDashboardLinkPrefix from '../../../../hooks/useDashboardLinkPrefix';
import {
  updatePersonalDocument as apiUpdatePersonalDocument,
  updatePersonalDocumentDirectory,
} from '../../../../api/propertyowners';
import { sGetPersonalFoldersBreadCrumbs } from '../../../../store/reducers/personalDocuments';
import {
  acAddPersonalFolder,
  acDeletePersonalDocumentFolder,
  acUpdatePersonalDocument,
} from '../../../../store/actions/personalDocuments';
import useSnackbar from '../../../../hooks/useSnackbar';
import matomo from '../../../../utils/matomo';
import {
  updateDocument as apiUpdateDocument,
  updatePropertyDocumentDirectory,
} from '../../../../api/documents';
import {
  acAddPropertyFolder,
  acDeletePropertyDocumentFolder,
  acUpdateDocument,
} from '../../../../store/actions/documents';
import { sGetPropertyFoldersBreadCrumbs } from '../../../../store/reducers/documents';

const ListItemButton = ({
  classes,
  documentObject,
  isFolder,
  isFromPersonal,
  isPersonalDocuments,
  onFolderChange,
  newUpload,
  docTitle,
  renderedIcon,
  headerHeight,
  handleDragOver,
  personalFoldersBreadCrumbs,
  propertyFoldersBreadCrumbs,
  deletePersonalDocumentFolder,
  deletePropertyDocumentFolder,
  updatePersonalDocument,
  updateDocument,
  addPersonalFolder,
  addPropertyFolder,
  children,
  publicSection = false,
}) => {
  const { boligmappaNumber } = useParams();
  const [isDragged, setIsDragged] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragShiftX = 30;
  const dragShiftY = 75;

  const dragRef = useRef(null);
  const dragTimeoutRef = useRef(null);
  const isSwipingRef = useRef(false);

  const { showSuccessMessage, showErrorMessage } = useSnackbar();

  const trackPersonalMatomoEvents = (action) => {
    matomo.clickEvent({
      category: 'Document handling',
      action,
    });
  };

  const dashboardLinkPrefix = useDashboardLinkPrefix();

  const documentLink = `${dashboardLinkPrefix}/documents/${getDocumentId(
    documentObject
  )}`;

  const personalDocumentPath = `/personal-documents/${getDocumentId(
    documentObject
  )}`;

  useEffect(() => {
    const handleTouchMove = (event) => {
      if (isDragged) {
        event.preventDefault(); // Prevents scrolling during drag
      }
    };
    // Add the touchmove listener with passive set to false
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isDragged]);

  const handleDrop = async (destinationItem) => {
    if (
      destinationItem.id !== documentObject.id &&
      isDocumentsFolder(destinationItem)
    ) {
      if (isDocumentsFolder(documentObject)) {
        const patchData = {
          parentId: destinationItem.id,
        };
        const updateDocumentDirectoryRequest = isPersonalDocuments
          ? updatePersonalDocumentDirectory
          : updatePropertyDocumentDirectory;
        const [requestError, response] = await updateDocumentDirectoryRequest(
          patchData,
          documentObject.id,
          boligmappaNumber
        );
        if (response && !requestError) {
          if (isPersonalDocuments) {
            deletePersonalDocumentFolder(documentObject);
            addPersonalFolder(
              [...personalFoldersBreadCrumbs, destinationItem],
              documentObject
            );
          } else {
            deletePropertyDocumentFolder(documentObject);
            addPropertyFolder(
              [...propertyFoldersBreadCrumbs, destinationItem],
              documentObject
            );
          }
          showSuccessMessage(
            `Flyttet "${documentObject.title || documentObject.name}" til "${
              destinationItem.name
            }"`
          );
        } else {
          showErrorMessage('Mappenavnet finnes allerede');
        }
      } else {
        const data = { ...documentObject, directoryId: destinationItem.id };
        const patchData = {
          directoryId: destinationItem.id,
        };
        const updateRequest = () =>
          isPersonalDocuments
            ? apiUpdatePersonalDocument(documentObject.id, patchData)
            : apiUpdateDocument(documentObject.id, {
                ...documentObject,
                ...patchData,
              });
        if (isPersonalDocuments) {
          updatePersonalDocument(data);
        } else {
          updateDocument(data);
        }
        const [error, response] = await updateRequest();
        if (!response && error) {
          showErrorMessage(error);
        } else {
          const folderId = patchData.directoryId;
          updatePersonalDocument({ ...documentObject, directoryId: folderId });
          showSuccessMessage(
            `Flyttet "${documentObject.title || documentObject.name}" til "${
              destinationItem.name
            }"`
          );
          trackPersonalMatomoEvents(
            'Move Single Personal Document to a Directory'
          );
        }
      }
    }
  };

  const handleTouchStart = (e) => {
    isSwipingRef.current = false;

    const touch = e.touches[0];

    setPosition({
      x: touch.clientX,
      y: touch.clientY,
    });

    dragTimeoutRef.current = setTimeout(() => {
      if (!isSwipingRef.current) {
        setIsDragged(true);
      }
    }, 300);
  };
  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const { clientX, clientY } = touch;

    const hoveredElement = document
      .elementFromPoint(clientX, clientY)
      .closest('.mobile-list-item');
    if (isDragged) {
      handleDragOver(hoveredElement);
    }

    const deltaX = clientX;
    const deltaY = clientY;

    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      isSwipingRef.current = true;
      clearTimeout(dragTimeoutRef.current); // Cancel drag if user is swiping
    }
    if (isDragged) {
      setPosition({
        x: clientX,
        y: clientY,
      });

      const scrollMargin = 50;
      const scrollSpeed = 10;
      if (touch.clientY < headerHeight + scrollMargin) {
        // Scroll up
        window.scrollBy(0, -scrollSpeed);
      } else if (touch.clientY > window.innerHeight - scrollMargin) {
        // Scroll down
        window.scrollBy(0, scrollSpeed);
      }
    }
  };
  const handleTouchEnd = (e) => {
    const hoveredElement = document
      .elementFromPoint(
        e.changedTouches[0].clientX,
        e.changedTouches[0].clientY
      )
      .closest('.mobile-list-item');

    if (
      isDragged &&
      hoveredElement &&
      hoveredElement.dataset &&
      hoveredElement.dataset.dropTarget
    ) {
      const { dropDocument } = hoveredElement;
      handleDrop(dropDocument);
    }

    setIsDragged(false);
    setPosition({ x: 0, y: 0 });
    clearTimeout(dragTimeoutRef.current);
    isSwipingRef.current = true;
    handleDragOver(null);
  };
  return (
    <div
      className={clsx([classes.listItemButton, publicSection && 'public'])}
      onTouchStart={(e) =>
        (isPersonalDocuments || !isPersonalDocuments) &&
        (!isFolder || !documentObject?.isSystemGenerated) &&
        !publicSection &&
        handleTouchStart(e)
      }
      onDragStart={(e) => e.preventDefault()}
      onTouchMove={(e) =>
        (isPersonalDocuments || !isPersonalDocuments) && handleTouchMove(e)
      }
      onTouchEnd={(e) =>
        (isPersonalDocuments || !isPersonalDocuments) && handleTouchEnd(e)
      }
      onContextMenu={(e) => e.preventDefault()}
    >
      {isFolder ? (
        <button
          type="button"
          className={classes.linkText}
          onClick={() => onFolderChange(documentObject.id)}
        >
          {children}
        </button>
      ) : (
        <Link
          className={classes.linkText}
          to={{
            pathname: isFromPersonal ? personalDocumentPath : documentLink,
            state: { newUpload, isPersonalDocuments, isFromPersonal },
          }}
        >
          {children}
        </Link>
      )}
      <div
        className={clsx([classes.dragElement, isDragged && 'dragged'])}
        ref={dragRef}
        style={{
          position: 'fixed',
          left: `${position.x - dragShiftX}px`,
          top: `${position.y - dragShiftY}px`,
          userSelect: 'none',
        }}
      >
        <span
          className={clsx([
            classes.imageIconBox,
            getDocumentFileType(documentObject),
          ])}
        >
          {renderedIcon}
        </span>
        <p>{docTitle}</p>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  personalFoldersBreadCrumbs: sGetPersonalFoldersBreadCrumbs(state),
  propertyFoldersBreadCrumbs: sGetPropertyFoldersBreadCrumbs(state),
});

const mapDispatchToProps = (dispatch) => ({
  updatePersonalDocument: (payload) =>
    dispatch(acUpdatePersonalDocument(payload)),
  updateDocument: (document) => dispatch(acUpdateDocument(document)),
  addPersonalFolder: (breadCrumbs, response) =>
    dispatch(acAddPersonalFolder(breadCrumbs, response)),
  addPropertyFolder: (breadCrumbs, response) =>
    dispatch(acAddPropertyFolder(breadCrumbs, response)),
  deletePersonalDocumentFolder: (folder) =>
    dispatch(acDeletePersonalDocumentFolder(folder)),
  deletePropertyDocumentFolder: (folder) =>
    dispatch(acDeletePropertyDocumentFolder(folder)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ListItemButton);
